import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { UserInfoFormData } from './UserInfoForm'
import type { LocationInfoFormData } from './LocationInfoForm'
import type { CarTypeFormData } from './CarTypeForm'
import type { AlternateInvoiceData } from './OrderForm'
import type { RegisteredCustomer } from '../../api/etApi'
import type { PriceEstimationDisplayProps } from './PriceEstimationDisplay'
import { getCountryByVorwahl } from '../countryPicker/Countries'

export interface PriceCalculatorFormState {
  priceCalculationState: PriceEstimationDisplayProps[] | undefined | 'requestOnly',
    formState: 'signInOrEnterUserDataFormVisible' | 'pickupLocationFormVisible' | 'destinationLocationFormVisible' | 'carTypeFormVisible' | 'orderFormVisible' | 'orderComplete' | 'orderError',
    signInOrEnterUserDataFormState: 'userDataFormVisible' | 'loginFormVisible' 
    orderingFormState: 'sendingOrder' | 'sendingOrderComplete' | 'sendingOrderError',
    accountState: 'begin' | 'loggedIn' | 'userDataSet' | 'waitingForMailConfirmation' | 'error'
    userInfoData: UserInfoFormData,
    pickupLocationData: LocationInfoFormData,
    destinationLocationData: LocationInfoFormData,
    carTypeData: CarTypeFormData,
    alternateInvoiceData?: AlternateInvoiceData,
    customerNote?: string
}

const initialState: PriceCalculatorFormState = {
    priceCalculationState: undefined,
    formState: 'pickupLocationFormVisible',
    signInOrEnterUserDataFormState: 'userDataFormVisible',
    orderingFormState: 'sendingOrder',
    accountState: 'begin',
    userInfoData:{
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        phonePrefixCountry: 138,
        address: "",
        password: "",
        zipcode: "",
        city: "",
        country: 138,
        passwordConfirm: "",
    },
    pickupLocationData:{
        streetNumber: "",
        city: "",
        country: "",
        zip: "",
        contactPerson: "",
        companyName: "",
        date: "",
        startHour: [null, null], 
    },
    destinationLocationData:{
        streetNumber: "",
        city: "",
        country: "",
        zip: "",
        contactPerson: "",
        companyName: "",
        date: "",        
        startHour: [null, null], 
    },
    carTypeData:{
        make: "",
        model: "",
        width: "",
        height: "",
        length: "",
        weight: "2000",
        licensePlate: "",
        carType: "2",
        carRegistered: true,
        specialOptions: [],
        tires: "",
        chassisNumber: ''
    },
    alternateInvoiceData: undefined,
    customerNote: undefined,
}

export const priceCalculatorFormSlice = createSlice({
    name: 'priceCalculatorForm',
    initialState,
    reducers: {
        updatePriceCalculationState: (state, action: PayloadAction<PriceEstimationDisplayProps[] | undefined | 'requestOnly'>) => {
          state.priceCalculationState = action.payload;
        },

        updateUserInfoDataFromApi: (state, action: PayloadAction<RegisteredCustomer>) => {
            state.userInfoData.address = action.payload.Address ?? "";
            state.userInfoData.city = action.payload.City ?? "";
            state.userInfoData.country = action.payload.Country ?? 138;
            state.userInfoData.email = action.payload.Email ?? "";
            const nameParts = (action.payload.Name ?? "").split(" ");
            state.userInfoData.firstName = nameParts[0] ?? "";
            state.userInfoData.lastName = nameParts[1] ?? "";
            state.userInfoData.zipcode = action.payload.Zip ?? "";
            state.userInfoData.phone = action.payload.Telephone?.replace(/\+{1}\d*\s*/, "").trim() || "";

            let matches = action.payload.Telephone?.match(/\+{1}\d*\s*/);
            if (matches && matches.length > 0) {
              const vorwahl = matches[0];
              const country = getCountryByVorwahl(vorwahl.trim());
              state.userInfoData.phonePrefixCountry = country?.COU_ID || 138;
            }
            else {
              state.userInfoData.phonePrefixCountry = 138;
            }
        },
        accountAlreadyExists: (state) => 
        {
          state.formState = "signInOrEnterUserDataFormVisible";
          state.signInOrEnterUserDataFormState = 'loginFormVisible';
          state.accountState = "begin";
        },

        /* Form data updates */
        updateUserInfoData: (state, action: PayloadAction<UserInfoFormData>) => {
            state.userInfoData = action.payload
        },
        updatePickupLocationData: (state, action: PayloadAction<LocationInfoFormData>) => {
            state.pickupLocationData = action.payload
        },
        updateDestinationLocationData: (state, action: PayloadAction<LocationInfoFormData>) => {
            state.destinationLocationData = action.payload
        },
        updateCarTypeData: (state, action: PayloadAction<CarTypeFormData>) => {
            state.carTypeData = action.payload
        },
        updateAlternateInvoiceData: (state, action: PayloadAction<AlternateInvoiceData | undefined>) => {
            state.alternateInvoiceData = action.payload
        },
        updateCustomerNote: (state, action: PayloadAction<string | undefined>) => {
            state.customerNote = action.payload
        },

        /* Transitions between individual form pages */
        signInOrEnterUserDataFormComplete: (state) => {
            if (state.formState !== 'signInOrEnterUserDataFormVisible') {
              console.error('signInOrEnterUserDataFormComplete called when formState is not signInOrEnterUserDataFormVisible')
              throw new Error('signInOrEnterUserDataFormComplete called when formState is not signInOrEnterUserDataFormVisible')
            }
            state.formState = 'orderFormVisible'

            //if the user is not logged in, we instead have user data
            if (state.accountState === 'begin') {
              state.accountState = 'userDataSet';
            }
        },
        pickupLocationFormComplete: (state) => {
          if (state.formState !== 'pickupLocationFormVisible') {
            console.error('pickupLocationFormComplete called when formState is not pickupLocationFormVisible')
            throw new Error('pickupLocationFormComplete called when formState is not pickupLocationFormVisible')
          }
          state.formState = 'destinationLocationFormVisible'
        },
        destinationLocationFormComplete: (state) => {
          if (state.formState !== 'destinationLocationFormVisible') {
            console.error('destinationLocationFormComplete called when formState is not destinationLocationFormVisible')
            throw new Error('destinationLocationFormComplete called when formState is not destinationLocationFormVisible')
          }
          state.formState = 'carTypeFormVisible'
        },
        carTypeFormComplete: (state) => {
          if (state.formState !== 'carTypeFormVisible') {
            console.error('carTypeFormComplete called when formState is not carTypeFormVisible')
            throw new Error('carTypeFormComplete called when formState is not carTypeFormVisible')
          }
          state.formState = 'signInOrEnterUserDataFormVisible'
        },
        orderFormComplete: (state) => {
          if (state.formState !== 'orderFormVisible') {
            console.error('orderFormComplete called when formState is not orderFormVisible')
            throw new Error('orderFormComplete called when formState is not orderFormVisible')
          }
          state.formState = 'orderComplete'
        },
        orderFormError: (state) => {
          if (state.formState !== 'orderFormVisible') {
            console.error('orderFormError called when formState is not orderFormVisible')
            throw new Error('orderFormError called when formState is not orderFormVisible')
          }
          state.formState = 'orderError'
        },
        goBackToPreviousForm: (state) => {
          switch (state.formState) {
            case 'orderFormVisible':
              state.formState = 'signInOrEnterUserDataFormVisible';
              break;
            case 'signInOrEnterUserDataFormVisible':
              state.formState = 'carTypeFormVisible';
              break;
            case 'carTypeFormVisible':
              state.formState = 'destinationLocationFormVisible';
              break;
            case 'destinationLocationFormVisible':
              state.formState = 'pickupLocationFormVisible';
              break;
            default:
              console.error('goBackToPreviousForm called when formState is not one of the expected states');
              throw new Error('goBackToPreviousForm called when formState is not one of the expected states');
          }
        },
        switchToPickupLocationForm: (state) => {
          state.formState = 'pickupLocationFormVisible'
        },
        switchToDestinationLocationForm: (state) => {
          state.formState = 'destinationLocationFormVisible'
        },
        switchToLoginForm: (state) => {
          if (state.signInOrEnterUserDataFormState !== 'userDataFormVisible') {
            console.error('switchToLoginForm called when signInOrEnterUserDataFormState is not userDataFormVisible')
            throw new Error('switchToLoginForm called when signInOrEnterUserDataFormState is not userDataFormVisible')
          }
          state.signInOrEnterUserDataFormState = 'loginFormVisible'
        },
        switchToUserDataForm: (state) => {
          if (state.signInOrEnterUserDataFormState !== 'loginFormVisible') {
            console.error('switchToUserDataForm called when signInOrEnterUserDataFormState is not loginFormVisible')
            throw new Error('switchToUserDataForm called when signInOrEnterUserDataFormState is not loginFormVisible')
          }
          state.signInOrEnterUserDataFormState = 'userDataFormVisible'
        },

        /* Transitions regarding the account state */
        logout: (state) => {
          if (state.accountState !== 'loggedIn') {
            console.error('logout called when accountState is not loggedIn, userDataSet or waitingForMailConfirmation')
            throw new Error('logout called when accountState is not loggedIn, userDataSet or waitingForMailConfirmation')
          }
          state.accountState = 'begin';
          state.formState = 'signInOrEnterUserDataFormVisible';
          state.signInOrEnterUserDataFormState = 'userDataFormVisible';
        },
        login: (state) => {
            state.signInOrEnterUserDataFormState = 'userDataFormVisible';
            state.accountState = 'loggedIn'
            // NOTE: Dont change formState here. If we are on the login page, we wanna stay there so the user can change their account details.
            // If we have instead created an account, the formState is already correct and we dont wanna go back
            // because the user surely doesnt wanna change their data now.
        },
        setUserData: (state) => {
            if (state.accountState !== 'begin') {
              console.error('setUserData called when accountState is not begin')
              throw new Error('setUserData called when accountState is not begin')
            }
          state.accountState = 'userDataSet'
        },
        setWaitingForMailConfirmation: (state) => {
            if (state.accountState !== 'userDataSet') {
              console.error('setWaitingForMailConfirmation called when accountState is not userDataSet')
              throw new Error('setWaitingForMailConfirmation called when accountState is not userDataSet')
            }
            state.accountState = 'waitingForMailConfirmation'
        },
        setError: (state) => {
            state.accountState = 'error'
        },

        /* Transitions regarding the ordering form state */
        sendingOrderComplete: (state) => {
            if (state.orderingFormState !== 'sendingOrder') {
              console.error('sendingOrderComplete called when orderingFormState is not sendingOrder')
              throw new Error('sendingOrderComplete called when orderingFormState is not sendingOrder')
            }
            state.orderingFormState = 'sendingOrderComplete'
        },
        sendingOrderError: (state) => {
            if (state.orderingFormState !== 'sendingOrder') {
              console.error('sendingOrderError called when orderingFormState is not sendingOrder')
              throw new Error('sendingOrderError called when orderingFormState is not sendingOrder')
            }
            state.orderingFormState = 'sendingOrderError'
        },
    },
});

export const {
    updatePriceCalculationState,
    accountAlreadyExists,
    logout,
    orderFormComplete,
    orderFormError,
    signInOrEnterUserDataFormComplete,
    pickupLocationFormComplete,
    destinationLocationFormComplete,
    carTypeFormComplete,
    goBackToPreviousForm,
    switchToLoginForm,
    switchToUserDataForm,
    switchToDestinationLocationForm,
    switchToPickupLocationForm,
    login,
    setUserData,
    setWaitingForMailConfirmation,
    setError,
    sendingOrderComplete,
    sendingOrderError,
    updateUserInfoData,
    updatePickupLocationData,
    updateDestinationLocationData,
    updateCarTypeData,
    updateAlternateInvoiceData,
    updateCustomerNote,
    updateUserInfoDataFromApi,
} = priceCalculatorFormSlice.actions
export default priceCalculatorFormSlice.reducer